export interface RedactOptions {
  /** Field names to redact (case-insensitive). Replaces value with placeholder. */
  redactKeys?: string[];
  /** Regex patterns applied to string values (e.g. CF, IBAN, JWT). */
  redactValuePatterns?: RegExp[];
  /** Replacement for redacted values. */
  placeholder?: string;
  /** Strings longer than this matching base64 charset are collapsed to `[base64 Nb]`. */
  base64MinLength?: number;
  /** Walk depth limit to prevent runaway serialization. */
  maxDepth?: number;
}

/* eslint-disable sonarjs/no-hardcoded-passwords -- these are field NAMES to redact, not secrets */
export const DEFAULT_REDACT_KEYS = [
  'password',
  'pwd',
  'passwd',
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'bearer',
  'authorization',
  'auth',
  'apikey',
  'apisecret',
  'secret',
  'sessionkey',
  'sessionid',
  'cookie',
  'otp',
  'pin',
  'cvv',
  'cvc',
  'fiscalcode',
  'codicefiscale',
  'taxid',
  'iban',
  'pan',
  'cardnumber',
  'accountnumber',
];
/* eslint-enable sonarjs/no-hardcoded-passwords */

export const DEFAULT_VALUE_PATTERNS: RegExp[] = [
  /\b[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]\b/g,
  /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g,
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
];

interface ResolvedOptions {
  keys: string[];
  patterns: RegExp[];
  placeholder: string;
  base64Min: number;
  maxDepth: number;
}

function resolveOptions(opts: RedactOptions): ResolvedOptions {
  return {
    keys: (opts.redactKeys ?? DEFAULT_REDACT_KEYS).map((k) => k.toLowerCase()),
    patterns: opts.redactValuePatterns ?? DEFAULT_VALUE_PATTERNS,
    placeholder: opts.placeholder ?? '***',
    base64Min: opts.base64MinLength ?? 512,
    maxDepth: opts.maxDepth ?? 8,
  };
}

function redactString(s: string, resolved: ResolvedOptions): string {
  if (s.length >= resolved.base64Min && /^[A-Za-z0-9+/=]+$/.test(s)) {
    return `[base64 ${s.length}b]`;
  }
  let out = s;
  for (const re of resolved.patterns) {
    out = out.replace(re, resolved.placeholder);
  }
  return out;
}

export function redact(value: unknown, opts: RedactOptions = {}): unknown {
  const resolved = resolveOptions(opts);
  const seen = new WeakSet<object>();

  const walk = (v: unknown, depth: number): unknown => {
    if (depth > resolved.maxDepth) return '[max-depth]';
    if (v === null || v === undefined) return v;
    if (typeof v === 'string') return redactString(v, resolved);
    if (typeof v !== 'object') return v;
    if (seen.has(v as object)) return '[circular]';
    seen.add(v as object);

    if (Array.isArray(v)) {
      return v.map((item) => walk(item, depth + 1));
    }

    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).map(([k, val]) => {
        if (resolved.keys.includes(k.toLowerCase())) {
          return [k, resolved.placeholder];
        }
        return [k, walk(val, depth + 1)];
      }),
    );
  };

  return walk(value, 0);
}

/**
 * Redact + JSON.stringify + truncate. Returns a stringified payload safe to push to Faro.
 */
export function redactToJson(value: unknown, maxChars: number, opts?: RedactOptions): string {
  try {
    const json = JSON.stringify(redact(value, opts));
    if (json === undefined) return '';
    return json.length > maxChars ? json.slice(0, maxChars) + '…' : json;
  } catch {
    return '[unserializable]';
  }
}
